/* eslint-disable custom-rules/data-testid-required */
import React, { Children, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  chakra,
  Collapse,
  Divider,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  omitThemingProps,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useDisclosure,
  useFormControl,
  useMultiStyleConfig,
  useOutsideClick,
} from "@chakra-ui/react";
import { ChevronDownIcon, SearchIcon, XIcon } from "@heroicons/react/outline";
import propTypes from "prop-types";

import { MultiSelectProvider } from "./MultiSelectContext";

const containerStyles = {
  border: "1px solid",
  borderColor: "gray.300",
  borderRadius: "md",
  cursor: "text",
  display: "grid",
  paddingLeft: "4",
  gridTemplateColumns: "1fr 40px",
  transitionProperty: "var(--chakra-transition-property-common)",
  transitionDuration: "var(--chakra-transition-duration-normal)",
  width: "100%",
};

const tagContainerStyles = {
  alignItems: "center",
  display: "flex",
  gap: "1",
  flexWrap: "wrap",
};

const placeholderStyles = {
  color: "gray.700",
};

const dropdownContainerStyles = {
  backgroundColor: "white",
  border: "1px solid var(--chakra-colors-gray-300)",
  borderRadius: "0 0 4px 4px",
  boxShadow: "md",
  maxHeight: "400px",
  overflow: "auto !important",
  position: "absolute",
  width: "100%",
  zIndex: "1400",
};

const MultiSelect = (props) => {
  // Get theme styles
  const {
    dropdownContainer,
    icon,
    multiSelect,
    placeholder: placeholderTheme,
    tagContainer,
  } = useMultiStyleConfig("MultiSelect", props);

  // Destructure props and exclude theme props
  const { children, defaultValue, onChange, placeholder, value, ...rest } =
    omitThemingProps(props);
  const options = useMemo(
    () =>
      Children.map(Children.toArray(children), (c) => ({
        label: c.props.children,
        value: c.props.value,
      })),
    [children]
  );

  // State Hooks
  const [selectedValues, setSelectedValues] = useState(
    options?.filter(
      (o) => value?.includes(o.value) || defaultValue?.includes(o.value)
    )
  );

  const [filterValue, setFilterValue] = useState("");

  // Utility Hooks
  const { isOpen, onClose, onOpen, onToggle } = useDisclosure();
  // Gets information from the FormControl context if there is one.
  const { disabled, id: formControlId, name, readOnly } = useFormControl(props);
  const componentRef = useRef();

  useOutsideClick({
    handler: (event) => {
      if (event.target) onClose();
    },
    ref: componentRef,
  });

  // If the component is being controlled we need to update the inner state to match the 'value' prop
  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedValues(options?.filter((o) => value?.includes(o.value)));
    }
  }, [options, value]);

  // Event Handlers
  const handleCheckboxClick = (newValue, event) => {
    if (!Array.isArray(value)) {
      setSelectedValues(newValue);
    }

    if (typeof onChange === "function") {
      onChange(
        newValue.map((v) => v.value),
        event
      );
    }
  };

  const handleTagRemoveClick = (removedValue, event) => {
    event.stopPropagation();

    if (typeof onChange === "function") {
      onChange(
        selectedValues
          .filter((v) => v.value !== removedValue)
          .map((v) => v.value),
        event
      );
    }

    if (!Array.isArray(value)) {
      setSelectedValues((previousValue) =>
        previousValue.filter((v) => v.value !== removedValue)
      );
    }
  };

  const handleFilterChange = (value) => {
    setFilterValue(value);
  };

  return (
    <div ref={componentRef}>
      <chakra.div
        __css={containerStyles}
        onClick={onToggle}
        sx={multiSelect}
        {...rest}
      >
        <chakra.div __css={tagContainerStyles} sx={tagContainer}>
          {Array.isArray(selectedValues) && selectedValues.length > 0 ? (
            selectedValues.map((selectedValue) => (
              <Tag key={selectedValue.value} size={props.size}>
                <TagLabel>{selectedValue.label}</TagLabel>

                <TagCloseButton
                  onClick={(event) =>
                    handleTagRemoveClick(selectedValue.value, event)
                  }
                />
              </Tag>
            ))
          ) : (
            <Text
              __css={placeholderStyles}
              fontSize={props.size}
              sx={placeholderTheme}
            >
              {placeholder}
            </Text>
          )}
        </chakra.div>

        <IconButton
          aria-label="Open MultiSelect Dropdown"
          icon={<Icon as={ChevronDownIcon} />}
          onClick={onOpen}
          size="md"
          sx={icon}
          variant="unstyled"
        />
      </chakra.div>

      <chakra.div
        __css={dropdownContainerStyles}
        as={Collapse}
        in={isOpen}
        sx={dropdownContainer}
      >
        <Box
          padding="2"
          position="sticky"
          backgroundColor="gray.50"
          zIndex={1}
          top={0}
        >
          <InputGroup>
            <InputLeftElement>
              <Icon as={SearchIcon} color="gray.300" />
            </InputLeftElement>

            <Input
              data-testid={`skyview-core-multiselect-${rest.id}`}
              onChange={(e) => handleFilterChange(e.target.value)}
              value={filterValue}
            />

            {filterValue && (
              <InputRightElement>
                <IconButton
                  aria-label="Clear Search"
                  onClick={() => handleFilterChange("")}
                  size="sm"
                  variant="ghost"
                >
                  <Icon as={XIcon} color="gray.300" fontSize="1.5em" />
                </IconButton>
              </InputRightElement>
            )}
          </InputGroup>

          <Divider marginTop="2" opacity="1" />
        </Box>

        <MultiSelectProvider
          value={{
            disabled,
            filter: filterValue,
            id: formControlId,
            name: name,
            onChange: handleCheckboxClick,
            readOnly,
            value: selectedValues,
          }}
        >
          {children}
        </MultiSelectProvider>
      </chakra.div>
    </div>
  );
};

MultiSelect.displayName = "MultiSelect";

MultiSelect.propTypes = {
  children: propTypes.node,
  defaultValue: propTypes.arrayOf(propTypes.string),
  id: propTypes.string,
  name: propTypes.string,
  onChange: propTypes.func,
  size: propTypes.string,
  value: propTypes.arrayOf(propTypes.string),
};

export default MultiSelect;
